import {
  useCreateScheduleReadCycle,
  useGetAllReadCycle,
  useGetFrequency,
  useGetMeterScheduleDetails,
  useGetMonths,
  useGetScheduleReview,
  useGetWeekDays,
  useUpdateScheduleReadCycle,
} from "@features/mx/readCycleManagement/hooks";
import { logEvent } from "@shared/analytics/analytics";
import { DynamicForm } from "@shared/components/DynamicForm";
import { GenericTable } from "@shared/components/GenericTable";
import { PageHeader } from "@shared/components/PageHeader";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { FormField, FormService } from "@shared/services/FormServices";
import { TableColumn } from "@shared/types/table";
import { normalizeDate } from "@shared/utils/dateUtils";
import { ArrowLeft } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const parseBackendDate = (dateStr?: string) => {
  if (!dateStr) return "";
  if (/^\d{2}[\/\-]\d{2}[\/\-]\d{4}$/.test(dateStr)) {
    const [day, month, year] = dateStr.split(/[\/\-]/);
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }
  return normalizeDate(dateStr);
};

const generateDaysOfMonthOptions = () => {
  const options = [];
  for (let day = 1; day <= 29; day++) {
    options.push({ label: day.toString(), value: day });
  }
  return options;
};

const ScheduleCreate = () => {
  const navigate = useNavigate();
  const { action, schedule_id } = useParams();
  const isEditMode = action === "edit" && schedule_id;
  const remoteUtilityId = useRemoteUtilityId();
  const [frequency, setFrequency] = useState("");
  const [startDate, setStartDate] = useState("");
  const [time, setTime] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [formData, setFormData] = useState({
    readCycleId: "",
    cycleDuration: 30,
    frequency: "",
    start_date: "",
    time: "",
    end_date: "",
    day_of_week: "",
    month_of_year: "",
    day_of_month: "",
  });

  const { data: scheduleDetails } = useGetMeterScheduleDetails(
    isEditMode ? schedule_id : undefined
  );
  const { data: frequencyData } = useGetFrequency();
  const { data: weekDaysData } = useGetWeekDays();
  const { data: monthsData } = useGetMonths();
  const { data: scheduleReviewData } = useGetScheduleReview(
    frequency,
    startDate,
    time,
    endDate
  );
  const createScheduleReadCycle = useCreateScheduleReadCycle();
  const updateScheduleReadCycle = useUpdateScheduleReadCycle(schedule_id);
  const { data: readCycleData } = useGetAllReadCycle("unscheduled");

  const removeSeconds = (t?: string) => {
    if (!t) return "";
    const parts = t.split(":");
    return parts.length >= 2 ? `${parts[0]}:${parts[1]}` : "";
  };

  const parseTime = useCallback((t?: string) => {
    if (!t) return "";
    const d = new Date(`1970-01-01T${t}`);
    return isNaN(d.getTime())
      ? ""
      : `${String(d.getHours()).padStart(2, "0")}:${String(
          d.getMinutes()
        ).padStart(2, "0")}:${String(d.getSeconds()).padStart(2, "0")}`;
  }, []);

  const getFrequencyOptions = useMemo(() => {
    if (!frequencyData || !Array.isArray(frequencyData)) {
      return [];
    }
    return frequencyData.map((frequency) => ({
      value: frequency.key,
      label: frequency.value,
    }));
  }, [frequencyData]);

  const getWeekDaysOptions = useMemo(() => {
    if (!weekDaysData || !Array.isArray(weekDaysData)) {
      return [];
    }
    return weekDaysData.map((weekDay) => ({
      value: weekDay.key,
      label: weekDay.value,
    }));
  }, [weekDaysData]);

  const getMonthsOptions = useMemo(() => {
    if (!monthsData || !Array.isArray(monthsData)) {
      return [];
    }
    return monthsData.map((month) => ({
      value: month.key,
      label: month.value,
    }));
  }, [monthsData]);

  const getReadCycleOptions = useMemo(() => {
    if (!readCycleData || !Array.isArray(readCycleData)) {
      return [];
    }
    return readCycleData.map((readCycle) => ({
      value: readCycle.id,
      label: readCycle.meterCycleName,
    }));
  }, [readCycleData]);

  const formFields: FormField[] = [
    {
      name: "readCycleId",
      type: "select",
      label: "Select Read Cycle (not yet scheduled)",
      options: getReadCycleOptions,
      groupColorNeeded: false,
      placeholder: "Select read cycle...",
      required: false,
      showHelperTooltip: true,
      className: "col-span-2 mt-3",
      showWhen: (formValues: any) => {
        return action === "create";
      },
      helperText:
        "Only read cycles without a defined next run and with zero runs are shown here.",
    },
    {
      name: "readCycleName",
      type: "text",
      label: "Read Cycle Name",
      groupColorNeeded: false,
      placeholder: "Select read cycle...",
      required: false,
      showHelperTooltip: true,
      disabled: Boolean(isEditMode),
      className: "col-span-2 mt-3",
      showWhen: (formValues: any) => {
        return action === "edit";
      },
      helperText:
        "Only read cycles without a defined next run and with zero runs are shown here.",
    },
    {
      name: "frequency",
      label: "Frequency",
      type: "select",
      required: true,
      options: getFrequencyOptions,
      placeholder: "Select frequency...",
      fullWidth: true,
      defaultValue: "",
      helperText: "Choose how often the read cycle should occur",
      className: "col-span-1 mt-3",
    },
    {
      name: "start_date",
      label: "Start Date",
      type: "datepicker",
      required: true,
      placeholder: "Select start date...",
      fullWidth: true,
      defaultValue: "",
      helperText: "Select when the schedule should start",
      className: "col-span-1 mt-3",
    },
    {
      name: "time",
      label: "Start Time",
      type: "time",
      required: true,
      placeholder: "Select time...",
      fullWidth: true,
      helperText: "Select what time the cycle should run",
      className: "col-span-1 mt-3",
    },
    {
      name: "end_date",
      label: "Schedule End Date(last date to creating new run)",
      type: "datepicker",
      required: false,
      placeholder: "Select end date (optional)...",
      fullWidth: true,
      defaultValue: "",
      helperText: "Optional: Select when the schedule should end",
      className: "col-span-1 mt-3",
    },
    {
      name: "day_of_week",
      label: "Day of the Week",
      type: "select",
      required: false,
      options: getWeekDaysOptions,
      placeholder: "Select day of week...",
      fullWidth: true,
      defaultValue: "",
      helperText: "Select which day of the week to run the cycle",
      showWhen: (formValues: any) => {
        return formValues.frequency === "1" || formValues.frequency === "2";
      },
      className: "col-span-1 mt-3",
    },
    {
      name: "month_of_year",
      label: "Month",
      type: "select",
      required: false,
      options: getMonthsOptions,
      placeholder: "Select month...",
      fullWidth: true,
      defaultValue: "",
      helperText: "Select which month to run the cycle",
      showWhen: (formValues: any) => {
        return (
          formValues.frequency === "6" ||
          formValues.frequency === "7" ||
          formValues.frequency === "8"
        );
      },
      className: "col-span-1 mt-3",
    },
    {
      name: "day_of_month",
      label: "Day of the Month",
      type: "select",
      required: false,
      options: generateDaysOfMonthOptions(),
      placeholder: "Select day of month...",
      fullWidth: true,
      defaultValue: "",
      helperText: "Select which day of the month to run the cycle (1-29)",
      showWhen: (formValues: any) => {
        return (
          formValues.frequency === "3" ||
          formValues.frequency === "4" ||
          formValues.frequency === "6" ||
          formValues.frequency === "7" ||
          formValues.frequency === "8"
        );
      },
      className: "col-span-1 mt-3",
    },
    {
      name: "cycleDuration",
      type: "number",
      label: "Cycle Duration(days)",
      defaultValue: 30,
      placeholder: "Enter cycle duration...",
      required: true,
      min: 1,
      max: 365,
      showHelperTooltip: true,
      tooltipText:
        "Meaning: Duration that the reading window remains open. Use: Helps align workforce planning and reading periods. Calculation: Reading period end = start date + duration.",
      className: "col-span-2 mt-3",
    },
    {
      name: "preview",
      label:
        "Preview of Next 5 Runs (based on your schedule and cycle duration)",
      type: "custom",
      className: "col-span-2",
      required: false,
      defaultValue: "",
      customRender: ({ field }: any) => {
        // Transform the data to create unique keys
        const transformedData =
          scheduleReviewData?.map((item: any, index: number) => ({
            runNumber: item.runNumber,
            startDatetime: item.startDatetime,
            remainingTime: item.remainingTime,
            readingPeriodStart: item.startDatetime, // Same data, different key
            endDatetime: item.endDatetime,
          })) || [];

        const columns: TableColumn<any>[] = [
          {
            key: "runNumber",
            header: "Run #",
            sortable: true,
          },
          {
            key: "startDatetime",
            header: "Run Date",
            sortable: true,
          },
          {
            key: "remainingTime",
            header: "Remaining Time",
            sortable: true,
          },
          {
            key: "readingPeriodStart", // Now it's a unique field in transformed data
            header: "Reading Period Start",
            sortable: true,
          },
          {
            key: "endDatetime",
            header: "Reading Window Ends",
            sortable: true,
          },
        ];

        return (
          <div className="space-y-4">
            <GenericTable
              data={transformedData}
              columns={columns}
              loading={false}
              rowKey="runNumber"
              emptyMessage={`No data found`}
            />
          </div>
        );
      },
    },
  ];

  // Create form instance
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: formData,
  });

  const handleFieldChange = (field: string, value: any) => {
    switch (field) {
      case "frequency":
        setFrequency(value);
        break;
      case "start_date":
        setStartDate(value);
        break;
      case "time":
        setTime(parseTime(value));
        break;
      case "end_date":
        setEndDate(value);
        break;
    }
  };

  useEffect(() => {
    if (isEditMode && scheduleDetails?.[0] && !isDataLoaded) {
      const data = scheduleDetails?.[0];

      // Prepare populated data following the Create component pattern
      const populatedData = {
        readCycleName: data.meterCycleName,
        cycleDuration: data.cycleDuration ? parseInt(data.cycleDuration) : 30,
        frequency: data?.frequency?.toString() || "",
        start_date: parseBackendDate(data?.startDate),
        time: data?.extraData?.time,
        end_date: parseBackendDate(data?.endDate),
        day_of_week: "",
        month_of_year: "",
        day_of_month: "",
      };

      // Handle conditional fields based on frequency
      const frequency = data?.frequency;
      if (frequency !== undefined) {
        switch (parseInt(frequency.toString())) {
          // Daily or Weekly
          case 1:
          case 2:
            if (data?.extraData?.dayOrMonth !== undefined) {
              populatedData.day_of_week =
                data?.extraData?.dayOrMonth?.toString();
            }
            break;

          // Monthly, Quarterly
          case 3:
          case 4:
            if (data?.extraData?.dayOfMonth !== undefined) {
              populatedData.day_of_month =
                data?.extraData?.dayOfMonth?.toString();
            }
            if (data?.extraData?.dayOrMonth !== undefined) {
              populatedData.month_of_year =
                data?.extraData?.dayOrMonth?.toString();
            }
            break;

          // Annually, Bi-annually, Custom
          case 6:
          case 7:
          case 8:
            if (data?.extraData?.dayOrMonth !== undefined) {
              populatedData.month_of_year =
                data?.extraData?.dayOrMonth?.toString();
            }
            if (data?.extraData?.dayOfMonth !== undefined) {
              populatedData.day_of_month =
                data?.extraData?.dayOfMonth?.toString();
            }
            break;
        }
      }

      // Use FormService.populateForm to populate the form
      FormService.populateForm(form, populatedData, formFields);
      setIsDataLoaded(true);
    }
  }, [isEditMode, scheduleDetails, isDataLoaded, form, formFields]);

  const handleSubmit = async () => {
    const formData = form.getValues();
    let payload: any = {
      meter_cycle:
        action === "edit"
          ? scheduleDetails?.[0]?.meterCycle
          : Number(formData.readCycleId),
      frequency: parseInt(formData.frequency),
      time: removeSeconds(formData.time),
      start_date: formData.start_date,
      due_days: Number(formData.cycleDuration),
      remote_utility_id: Number(remoteUtilityId),
    };

    // Add due_date from start_date
    if (formData.start_date) {
      payload.due_date = formData.start_date;
    }

    // Add end_date if provided
    if (formData.end_date) {
      payload.end_date = formData.end_date;
    }

    // Handle frequency-specific transformations
    const frequencyEdit = parseInt(formData.frequency);

    switch (frequencyEdit) {
      case 1: // Daily
      case 2: // Weekly
        if (formData.day_of_week) {
          payload.day_or_month = parseInt(formData.day_of_week);
        }
        break;

      case 3: // Monthly
      case 4: // Quarterly
        if (formData.day_of_month) {
          payload.day_of_month = parseInt(formData.day_of_month);
        }
        if (formData.month_of_year) {
          payload.day_or_month = parseInt(formData.month_of_year);
        }
        break;

      case 6: // Annually
      case 7: // Bi-annually
      case 8: // Custom
        if (formData.month_of_year) {
          payload.day_or_month = parseInt(formData.month_of_year);
        }
        break;

      case 9: // Event-based
        if (formData.event) {
          payload.event = formData.event;
        }
        break;

      case 0: // Once
      case 5: // Semi-annually
      default:
        // No additional fields needed for these frequencies
        break;
    }
    // Prepare base payload
    switch (action) {
      case "edit":
        // Call the mutation
        await updateScheduleReadCycle.mutateAsync(payload, {
          onSuccess: () => {
            logEvent("Read Cycle Schedule Started", payload);
          },
        });
        break;
      case "create":
        // Call the mutation
        await createScheduleReadCycle.mutateAsync(payload, {
          onSuccess: () => {
            logEvent("Read Cycle Schedule Started", payload);
          },
        });
        break;
    }

    handleClose();
  };

  const handleClose = () => {
    navigate("/mx/read-cycle/?tab=schedules");
  };
  return (
    <div>
      <div className="mb-4 flex items-center gap-4">
        <ArrowLeft
          className="w-5 h-5 cursor-pointer text-gray-700"
          onClick={() => navigate("/mx/read-cycle/?tab=schedules")}
        />
        <PageHeader
          title={isEditMode ? "Edit Schedule" : "Add Schedule"}
          subtitle="Configure schedule for an existing read cycle. This mirrors the scheduling section used during creation."
        />
      </div>
      <div className="mt-4">
        <DynamicForm
          fields={formFields}
          form={form}
          gridColumns={2}
          responsiveColumns={{ sm: 1, md: 2 }}
          onSubmit={handleSubmit}
          onReset={handleClose}
          onFieldChange={handleFieldChange}
          config={{
            resetButtonText: "Cancel",
            submitButtonText: "Save Schedule",
            layout: "grid",
            columns: 2,
            showSubmitButton: true,
            showResetButton: true,
            spacing: "medium",
            classes: {
              container: "w-full mb-4",
              label: "block text-sm font-medium text-gray-700 mb-2",
              buttonContainer: "flex justify-end",
              input:
                "w-full h-11 px-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all",
              select:
                "w-full h-11 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all",
              textarea:
                "w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all",
              error: "text-red-600 text-sm mt-1",
              helperText: "text-xs text-gray-500 mt-1",
              number:
                "w-full h-11 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all",
              "multi-select":
                "w-full h-11 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all",
              file:
                " w-full flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-12 text-center " +
                "hover:border-gray-400 transition-colors cursor-pointer bg-white",
              documents:
                "w-full flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-12 text-center " +
                "hover:border-gray-400 transition-colors cursor-pointer bg-white",
            },
          }}
        />
      </div>
    </div>
  );
};

export default ScheduleCreate;
